import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HttpError } from '@voll-intelligence/domain';

import {
  type MemberDetail,
  type MemberRole,
  type OrganizationRecord,
  membersRepository,
} from '@web/data/repositories';
import { t } from '@web/i18n';
import { useAuthz, useOrgContext, useSnackbar } from '@web/ui/core';
import { queryKeys } from '@web/core/query';

import { useMcpAccess } from './use_mcp_access';
import { fireAndForget } from '@web/ui/core/utils';

const NOW_MS = () => Date.now();
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export type RoleFilter = 'all' | MemberRole;
export type StatusFilter = 'all' | 'active' | 'inactive' | 'suspended';
export type SortMode = 'recent' | 'oldest' | 'name' | 'activity';

export interface MembersStats {
  total: number;
  newWeek: number;
  promoted: number;
  inactive: number;
}

interface UseMembersPageResult {
  isReady: boolean;
  members: MemberDetail[];
  filtered: MemberDetail[];
  stats: MembersStats;
  allDomains: string[];
  org: OrganizationRecord | null;
  canChangeRole: boolean;
  canChangeStatus: boolean;
  canRemove: boolean;
  canGrantMcp: boolean;
  query: string;
  setQuery: (v: string) => void;
  domain: string;
  setDomain: (v: string) => void;
  role: RoleFilter;
  setRole: (v: RoleFilter) => void;
  status: StatusFilter;
  setStatus: (v: StatusFilter) => void;
  sort: SortMode;
  setSort: (v: SortMode) => void;
  activeId: string | null;
  setActiveId: (v: string | null) => void;
  active: MemberDetail | null;
  changeRole: (userId: string, role: MemberRole) => void;
  setMemberStatus: (userId: string, status: 'active' | 'suspended', reason?: string) => void;
  removeMember: (userId: string) => void;
  toggleJit: () => void;
  setMcpAccess: (userId: string, enabled: boolean) => void;
}

function memberDomains(members: MemberDetail[]): string[] {
  return Array.from(
    new Set(
      members.map((member) => member.domain).filter((domain): domain is string => Boolean(domain)),
    ),
  ).sort((a, b) => a.localeCompare(b));
}

function memberCapabilities(authz: ReturnType<typeof useAuthz>): {
  canChangeRole: boolean;
  canChangeStatus: boolean;
  canRemove: boolean;
} {
  return {
    canChangeRole: authz.has('accounts:members:role-write'),
    canChangeStatus: authz.has('accounts:members:status-write'),
    canRemove: authz.has('accounts:members:remove'),
  };
}

export function useMembersPage(): UseMembersPageResult {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const authz = useAuthz();

  const listKey = ['members', 'list', orgId] as const;
  const orgKey = ['members', 'org', orgId] as const;

  const { data: members = [] } = useQuery({
    queryKey: listKey,
    queryFn: () => membersRepository.listMembers(orgId),
  });
  const { data: org = null } = useQuery({
    queryKey: orgKey,
    queryFn: () => membersRepository.getOrg(orgId),
  });

  const [query, setQuery] = useState('');
  const [domain, setDomain] = useState<string>('all');
  const [role, setRole] = useState<RoleFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<SortMode>('recent');
  const [activeId, setActiveId] = useState<string | null>(null);

  const allDomains = useMemo(() => memberDomains(members), [members]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filteredList = members.filter((m) => {
      if (domain !== 'all' && m.domain !== domain) return false;
      if (role !== 'all' && m.role !== role) return false;
      if (status !== 'all' && m.status !== status) return false;
      if (q && !`${m.name} ${m.email} ${m.id} ${m.userId}`.toLowerCase().includes(q)) return false;
      return true;
    });
    return [...filteredList].sort((a, b) => {
      if (sort === 'recent') return Date.parse(b.joinedAt) - Date.parse(a.joinedAt);
      if (sort === 'oldest') return Date.parse(a.joinedAt) - Date.parse(b.joinedAt);
      if (sort === 'name') return a.name.localeCompare(b.name);
      const av = a.lastSeen ? Date.parse(a.lastSeen) : 0;
      const bv = b.lastSeen ? Date.parse(b.lastSeen) : 0;
      return bv - av;
    });
  }, [members, query, domain, role, status, sort]);

  const stats = useMemo<MembersStats>(() => {
    const now = NOW_MS();
    const newWeek = members.filter((m) => now - Date.parse(m.joinedAt) <= 7 * ONE_DAY_MS).length;
    const promoted = members.filter((m) => m.role !== 'guest').length;
    const inactive = members.filter((m) => {
      if (m.status === 'inactive') return true;
      const seen = m.lastSeen ? Date.parse(m.lastSeen) : 0;
      return now - seen > 14 * ONE_DAY_MS;
    }).length;
    return { total: members.length, newWeek, promoted, inactive };
  }, [members]);

  const active = useMemo(
    () => members.find((m) => m.userId === activeId) ?? null,
    [members, activeId],
  );

  const errorMessage = (err: unknown): string =>
    err instanceof HttpError || err instanceof Error ? err.message : t('members.toast.error');

  const onMutationError = (err: unknown): void => {
    // A 403 here means the caller's permissions changed under us (e.g. demoted
    // by another admin). Drop the cached permission list so the UI re-syncs.
    if (err instanceof HttpError && err.status === 403) {
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.me.permissions(orgId) }));
    }
    showSnackbar({ message: errorMessage(err), severity: 'error' });
  };

  const invalidateList = (): Promise<void> => queryClient.invalidateQueries({ queryKey: listKey });
  const invalidateOrg = (): Promise<void> => queryClient.invalidateQueries({ queryKey: orgKey });

  const roleMutation = useMutation({
    mutationFn: (input: { userId: string; role: MemberRole }) =>
      membersRepository.setRole(orgId, input.userId, input.role),
    onSuccess: async (_data, input) => {
      await invalidateList();
      showSnackbar({
        message: t('members.toast.roleUpdated', { role: t(`members.role.${input.role}`) }),
        severity: 'success',
      });
    },
    onError: onMutationError,
  });

  const statusMutation = useMutation({
    mutationFn: (input: { userId: string; status: 'active' | 'suspended'; reason?: string }) =>
      membersRepository.setStatus(orgId, input.userId, {
        status: input.status,
        reason: input.reason,
      }),
    onSuccess: async () => {
      await invalidateList();
      showSnackbar({ message: t('members.toast.statusUpdated'), severity: 'success' });
    },
    onError: onMutationError,
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => membersRepository.remove(orgId, userId),
    onSuccess: async (_data, userId) => {
      if (activeId === userId) setActiveId(null);
      await invalidateList();
      showSnackbar({ message: t('members.toast.removed'), severity: 'success' });
    },
    onError: onMutationError,
  });

  const jitMutation = useMutation({
    mutationFn: (enabled: boolean) => membersRepository.setJitEnabled(orgId, enabled),
    onSuccess: async (_data, enabled) => {
      await invalidateOrg();
      showSnackbar({
        message: enabled ? t('members.toast.jitOn') : t('members.toast.jitOff'),
        severity: 'success',
      });
    },
    onError: onMutationError,
  });

  const mcpAccess = useMcpAccess({ orgId, members, invalidateList, onError: onMutationError });

  return {
    isReady: !!org,
    members,
    filtered,
    stats,
    allDomains,
    org,
    ...memberCapabilities(authz),
    ...mcpAccess,
    query,
    setQuery,
    domain,
    setDomain,
    role,
    setRole,
    status,
    setStatus,
    sort,
    setSort,
    activeId,
    setActiveId,
    active,
    changeRole: (userId, role) => {
      roleMutation.mutate({ userId, role });
    },
    setMemberStatus: (userId, status, reason) => {
      statusMutation.mutate({ userId, status, reason });
    },
    removeMember: (userId) => {
      removeMutation.mutate(userId);
    },
    toggleJit: () => {
      if (!org) return;
      jitMutation.mutate(!org.jitProvisioning.enabled);
    },
  };
}
